import { Radio, RadioGroup } from '@base-ui/react';
import styled from '@emotion/styled';

import type { RadioSize } from './types';

const SIZES: Record<RadioSize, { size: number; dotSize: number }> = {
  sm: { size: 16, dotSize: 8 },
  md: { size: 20, dotSize: 10 },
};

const noDollarProp = (prop: string) => !prop.startsWith('$');

export const StyledRadioGroup = styled(RadioGroup)({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
});

export const StyledRoot = styled(Radio.Root, { shouldForwardProp: noDollarProp })<{
  $size: RadioSize;
}>(({ theme, $size }) => {
  const { size } = SIZES[$size];

  return {
    // Layout & dimensions
    width: size,
    height: size,
    borderRadius: theme.radius.full,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    flexShrink: 0,
    boxSizing: 'border-box',
    padding: 0,
    WebkitTapHighlightColor: 'transparent',

    // Appearance — unchecked (default) state
    backgroundColor: theme.colors.background.overlay,
    border: `1px solid ${theme.colors.border.default}`,

    // The dot color is exposed as a custom property so the Indicator (a child)
    // can track the same hover/focus state without duplicating the selectors.
    '--radio-dot-color': theme.colors.brand.solid,

    // Behaviour
    cursor: 'pointer',
    outline: 'none',

    // Transitions
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',

    // Hover — unchecked
    '&:hover:not([data-disabled]):not([data-checked])': {
      borderColor: theme.colors.border.strong,
    },

    // Focus — unchecked
    '&:focus-visible:not([data-disabled]):not([data-checked])': {
      boxShadow: `0px 0px 0px 2px ${theme.colors.border.focus}`,
    },

    // Checked state
    '&[data-checked]': {
      border: `2px solid var(--radio-dot-color)`,
    },

    // Checked + hover / focus — deepen the color, same as unchecked hover/focus pairing
    '&[data-checked]:hover:not([data-disabled])': {
      '--radio-dot-color': theme.colors.brand.hover,
    },
    '&[data-checked]:focus-visible:not([data-disabled])': {
      '--radio-dot-color': theme.colors.brand.hover,
      boxShadow: `0px 0px 0px 2px ${theme.colors.border.focus}`,
    },

    // Disabled state
    '&[data-disabled]': {
      opacity: 0.5,
      cursor: 'not-allowed',
      pointerEvents: 'none',
    },
  };
});

export const StyledIndicator = styled(Radio.Indicator, { shouldForwardProp: noDollarProp })<{
  $size: RadioSize;
}>(({ theme, $size }) => {
  const { dotSize } = SIZES[$size];

  return {
    // Inner dot dimensions
    width: dotSize,
    height: dotSize,
    borderRadius: theme.radius.full,
    pointerEvents: 'none',
    backgroundColor: 'var(--radio-dot-color)',

    '&[data-unchecked]': {
      display: 'none',
    },
  };
});
